import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import React, { useState } from 'react';
import { FileCopy, List } from '@material-ui/icons';

import SerialNumberPopover from './SerialNumberPopover';

import useDataService from '../../hooks/useDataService';

import { eMessageType } from '../../types/IMessageType';
import { IGroupedDevices, IItem, IGeneratedDevice } from './types';

const CATEGORIES: IGeneratedDevice['testCategory'][] = [
  'Match',
  'Mismatch - Functional',
  'Mismatch - Non-Functional',
];

const CategoryChipColor: Record<string, 'default' | 'primary' | 'secondary' | undefined> = {
  Match: 'primary',
  'Mismatch - Functional': 'default',
  'Mismatch - Non-Functional': 'secondary',
};

interface IResultsTableProps {
  groupedDevices: IGroupedDevices;
  selectedItem: IItem | null;
}

const ResultsTable: React.FC<IResultsTableProps> = ({ groupedDevices, selectedItem }) => {
  const { openSnackBar } = useDataService();

  const [copiedCategory, setCopiedCategory] = useState<IGeneratedDevice['testCategory'] | null>(
    null
  );
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverCategory, setPopoverCategory] = useState<IGeneratedDevice['testCategory'] | null>(
    null
  );

  const totalDevices = CATEGORIES.reduce((sum, cat) => sum + groupedDevices[cat].totalCount, 0);

  const handleCopyAll = (cat: IGeneratedDevice['testCategory']) => {
    const text = groupedDevices[cat].serialItems.map((d) => d.serialId).join(', ');
    navigator.clipboard.writeText(text);
    setCopiedCategory(cat);
    openSnackBar('Copied to clipboard', eMessageType.success);
    setTimeout(() => setCopiedCategory(null), 2000);
  };

  const handleOpenPopover = (
    e: React.MouseEvent<HTMLElement>,
    cat: IGeneratedDevice['testCategory']
  ) => {
    setPopoverAnchor(e.currentTarget);
    setPopoverCategory(cat);
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setPopoverCategory(null);
  };

  return (
    <>
      <div className="cell">
        <div className="pxw-process-devices__results-header margin-bottom-1">
          <Typography variant="subtitle1">
            Test Results summary for <strong>{selectedItem?.itemNumber}</strong> — {totalDevices}{' '}
            devices
          </Typography>
        </div>

        <TableContainer
          component={Paper}
          variant="outlined"
          className="pxw-process-devices__table-container">
          <Table size="small" style={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 210 }}>
                  <strong>Category</strong>
                </TableCell>
                <TableCell style={{ width: 72 }}>
                  <strong>Qty</strong>
                </TableCell>
                <TableCell style={{ width: 140 }}>
                  <strong>Auto-generated</strong>
                </TableCell>
                <TableCell style={{ width: 120 }}>
                  <strong>Pre-existing</strong>
                </TableCell>
                <TableCell style={{ minWidth: 120 }}>
                  <strong>Serial Numbers</strong>
                </TableCell>
                <TableCell style={{ width: 48 }} />
                <TableCell style={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {CATEGORIES.map((cat) => {
                const group = groupedDevices[cat];
                if (group.serialItems.length === 0) return null;
                const autoCount = group.autoGeneratedCount;
                const serialsText = group.serialItems.map((d) => d.serialId).join(', ');
                return (
                  <TableRow key={cat}>
                    <TableCell>
                      <Chip label={cat} color={CategoryChipColor[cat]} size="small" />
                    </TableCell>
                    <TableCell>{group.totalCount}</TableCell>
                    <TableCell>{autoCount}</TableCell>
                    <TableCell>{group.preExistingCount}</TableCell>
                    <TableCell>
                      <div className="pxw-process-devices__serial-cell" title={serialsText}>
                        {serialsText}
                      </div>
                    </TableCell>
                    <TableCell padding="none">
                      <IconButton
                        size="small"
                        title={copiedCategory === cat ? 'Copied!' : 'Copy all serial numbers'}
                        onClick={() => handleCopyAll(cat)}>
                        <FileCopy fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell padding="none">
                      <IconButton
                        size="small"
                        title="View and search serial numbers"
                        onClick={(e) => handleOpenPopover(e, cat)}>
                        <List fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {popoverCategory && (
          <SerialNumberPopover
            anchor={popoverAnchor}
            category={popoverCategory}
            group={groupedDevices[popoverCategory]}
            onClose={handleClosePopover}
          />
        )}
      </div>
    </>
  );
};

export default ResultsTable;
